import type { apiRoutes } from '@socialfy/api-contract';
import type { ResponseData } from '@/api/types';
import { format } from '@/i18n/format';
import { fill } from '@/i18n/t';
import { VORGANG_TEXTE } from './texte';

/**
 * Reine Abbildungen der Vorgänge: Antwort von „Was passiert gerade“ → Klartextsätze, Takt der Abfrage und die
 * Zusammenfassung einer Zielgruppen-Bewertung aus dem Vergleich vorher/nachher.
 */

export type Activity = ResponseData<typeof apiRoutes.core.getActivity>;
export type ActivityProblem = Activity['problems'][number];
export type ActivityRun = Activity['running'][number];
export type ActivityRecent = Activity['recent'][number];

/** Takt der Abfrage, solange etwas läuft (ms). */
export const ACTIVITY_LIVE_MS = 5000;
/** Takt, wenn nichts läuft (ms): Probleme und neue Vorgänge erscheinen trotzdem ohne Neuladen. */
export const ACTIVITY_CALM_MS = 60_000;

export function activityPollMs(activity: Activity | undefined): number {
  return (activity?.running.length ?? 0) > 0 ? ACTIVITY_LIVE_MS : ACTIVITY_CALM_MS;
}

const DAY_MS = 86_400_000;
const T = VORGANG_TEXTE;

/** „heute um 03:00 Uhr“, „morgen um 03:00 Uhr“, sonst mit Datum (Berliner Zeit). */
function wannText(at: string, now: Date): string {
  const tag = format.date(at);
  const name =
    tag === format.date(now)
      ? T.problem.heute
      : tag === format.date(new Date(now.getTime() + DAY_MS))
        ? T.problem.morgen
        : tag;
  return fill(T.problem.wann, { tag: name, uhrzeit: format.time(at) });
}

/** Klartextsatz eines Problems, mit dem, was zu tun ist (oder dass es von selbst weitergeht). */
export function problemSatz(problem: ActivityProblem, now: Date = new Date()): string {
  const anzahl = format.number(problem.count ?? 0);
  const wann = problem.resumesAt === null ? T.problem.morgen : wannText(problem.resumesAt, now);
  switch (problem.code) {
    case 'audience_paused':
      return problem.count === null || problem.count === 0
        ? fill(T.problem.audience_paused, { wann })
        : fill(T.problem.audience_pausedAnzahl, { wann, anzahl });
    case 'checks_paused':
      return fill(T.problem.checks_paused, { wann });
    case 'service_login_failed':
      return fill(T.problem.service_login_failed, { anzahl });
    case 'service_unreachable':
      return fill(T.problem.service_unreachable, { anzahl });
    case 'judgments_failed':
      return problem.count === 1
        ? T.problem.judgments_failedEins
        : fill(T.problem.judgments_failed, { anzahl });
    default:
      return T.problem[problem.code];
  }
}

/** Reihenfolge der Wichtigkeit: was ohne Betreuer nicht geht, dann Pausen, dann zeitweise Störungen. */
const RANG: readonly ActivityProblem['code'][] = [
  'judgment_not_configured',
  'judgment_not_released',
  'budget_exhausted',
  'service_login_failed',
  'audience_paused',
  'ai_not_configured',
  'checks_paused',
  'judgments_failed',
  'service_unreachable',
  'maps_not_configured',
];

export function sortiereProbleme(problems: readonly ActivityProblem[]): ActivityProblem[] {
  return [...problems].sort((a, b) => RANG.indexOf(a.code) - RANG.indexOf(b.code));
}

export function wichtigstesProblem(problems: readonly ActivityProblem[]): ActivityProblem | null {
  return sortiereProbleme(problems)[0] ?? null;
}

/** Braucht das Problem den Betreuer (sonst geht es von selbst weiter)? */
export function brauchtBetreuer(problem: ActivityProblem): boolean {
  return [
    'judgment_not_configured',
    'judgment_not_released',
    'ai_not_configured',
    'maps_not_configured',
    'budget_exhausted',
    'service_login_failed',
  ].includes(problem.code);
}

/** Satz eines laufenden Vorgangs mit Stand. */
export function laufSatz(run: ActivityRun): string {
  const stand = run.progress;
  const zahlen =
    stand === null || stand.total === 0
      ? null
      : { fertig: format.number(stand.done), alle: format.number(stand.total) };
  switch (run.kind) {
    case 'audience_scoring':
      return zahlen === null
        ? fill(T.lauf.zielgruppe, { name: run.label ?? '' })
        : fill(T.lauf.zielgruppeStand, { name: run.label ?? '', ...zahlen });
    case 'web_search':
      return run.step === 'queued' || run.step === 'searching' || run.step === 'structuring'
        ? T.lauf.web[run.step]
        : T.lauf.web.searching;
    case 'paste_assess':
      return T.lauf.einfuegen;
    case 'processing':
      return zahlen === null ? T.lauf.aufbereitung : fill(T.lauf.aufbereitungStand, zahlen);
  }
}

/** Satz eines beendeten Vorgangs mit Zusammenfassung und Zeitpunkt. */
export function ergebnisSatz(recent: ActivityRecent, now: Date = new Date()): string {
  const t = recent.tally;
  let satz: string;
  switch (recent.kind) {
    case 'audience_scoring':
      satz = fill(T.ergebnis.zielgruppe, {
        name: recent.label ?? '',
        dabei: format.number(t.members ?? 0),
        pruefen: format.number(t.candidates ?? 0),
        nicht: format.number(t.excluded ?? 0),
      });
      break;
    case 'web_search':
      satz = fill(T.ergebnis.web[recent.outcome], { anzahl: format.number(t.found ?? 0) });
      break;
    default:
      satz = fill(T.ergebnis.einfuegen[recent.outcome], { anzahl: format.number(t.found ?? 0) });
  }
  return fill(T.ergebnis.wann, { satz, wann: format.relative(recent.finishedAt, now) });
}

export type ZielgruppenStand = { members: number; candidates: number; excluded: number };

/**
 * Zusammenfassung einer Bewertung aus dem Vergleich vorher/nachher: „3 neu dazu, 12 nicht dabei, 2 bitte prüfen.“
 * „neu dazu“/„nicht mehr dabei“ ist die Änderung der Mitglieder, „nicht dabei“ der Zuwachs der Ausgeschlossenen,
 * „bitte prüfen“ der Stand danach (das ist, was jetzt auf dich wartet). Ohne Stand vorher (der Lauf lief schon vor dem
 * ersten Blick) nur der Stand danach.
 */
export function zielgruppenZusammenfassung(
  vorher: ZielgruppenStand | null,
  nachher: ZielgruppenStand,
): string {
  const z = T.zusammenfassung;
  if (vorher === null) {
    return fill(z.stand, {
      dabei: format.number(nachher.members),
      pruefen: format.number(nachher.candidates),
      nicht: format.number(nachher.excluded),
    });
  }
  const teile: string[] = [];
  const mitglieder = nachher.members - vorher.members;
  if (mitglieder > 0) {
    teile.push(fill(z.neu, { anzahl: format.number(mitglieder) }));
  } else if (mitglieder < 0) {
    teile.push(fill(z.weg, { anzahl: format.number(-mitglieder) }));
  }
  const ausgeschlossen = nachher.excluded - vorher.excluded;
  if (ausgeschlossen > 0) {
    teile.push(fill(z.nichtDabei, { anzahl: format.number(ausgeschlossen) }));
  }
  if (teile.length === 0) {
    teile.push(z.keineAenderung);
  }
  if (nachher.candidates > 0) {
    teile.push(fill(z.pruefen, { anzahl: format.number(nachher.candidates) }));
  }
  return `${teile.join(', ')}.`;
}
